/**
 * Picking the renewal-schedule line that a chosen license SKU should be priced
 * and described against.
 *
 * A customer can hold several licenses from the same family at once — Nevmat
 * Australia bill both M365 Business Basic and Business Premium — and every
 * `BML.*` SKU matches the `licensing-m365` category, so "find the customer's
 * M365 line" is ambiguous for them. Matching the family and taking the first
 * hit picks whichever line the API happened to return first, which is how a
 * Business Basic selection ended up quoting Business Premium's product name and
 * renewal date on the ticket.
 *
 * The customer's own line for the *exact* SKU always wins. That is the line the
 * save-time sync increments (TicketEntryPage's schedule bump matches on
 * `productSku`), so the charge, the co-termination date and the product name on
 * the ticket all have to be read off the same row.
 *
 * Only when they hold no line for that SKU yet do we fall back to a sibling in
 * the same family, so a genuinely new license still co-terminates with the
 * cycle they're already on. Two things constrain that fallback:
 *
 *  - It only considers siblings billing on the **same cadence as the chosen
 *    SKU**. A monthly line always renews sooner than an annual one, so pooling
 *    both and taking the nearest date hands every annual license a monthly
 *    line's renewal date. An annual license has to co-terminate with an annual
 *    cycle or not at all — when the customer has no annual subscription yet,
 *    the right answer is "no match", which prices it at the full annual rate
 *    (the same conclusion `resolveAssignLicenseBilling` reaches).
 *  - Among the survivors it takes the soonest-renewing line rather than the
 *    first listed — `useRenewalSchedule` returns the API's order untouched, so
 *    array position carries no meaning.
 *
 * Paused lines are skipped throughout: a disabled schedule isn't billing, so it
 * can't set a renewal date (this matches `resolveAssignLicenseBilling` too).
 */
import type { RenewalScheduleItem } from '@techgitter/contracts';
import { getLicenseCategoryForSku, skuMatchesCategory } from '../data/licenseCategories';

export type LicenseTerm = 'monthly' | 'annual';

/**
 * The term a license SKU bills on, read off its suffix — the same rule
 * `resolveAssignLicenseBilling` uses, so the two can't disagree about whether a
 * selection is annual. SKUs carrying neither suffix (non-seat add-ons like
 * `BGL.AddStorage`) count as monthly.
 */
export function termForSku(sku: string): LicenseTerm {
  return sku.endsWith('.Annual') ? 'annual' : 'monthly';
}

/** RepairShopr frequencies are "Monthly" / "Annually". */
function billsOnTerm(item: RenewalScheduleItem, term: LicenseTerm): boolean {
  const frequency = item.frequency.toLowerCase();
  return term === 'annual' ? frequency === 'annually' : frequency === 'monthly';
}

/**
 * The customer's own active line for this exact SKU, if they hold one.
 *
 * Use this whenever the answer *names* the customer's existing subscription. A
 * same-family sibling is a different product — calling Business Premium "their
 * existing Business Standard subscription" is wrong however close the two sit
 * in the catalog, and the save-time schedule bump (which matches on
 * `productSku`) wouldn't touch that sibling either.
 */
export function findScheduleLineForExactSku(
  schedule: RenewalScheduleItem[],
  sku: string,
): RenewalScheduleItem | undefined {
  if (!sku) return undefined;
  return schedule.find((i) => !i.paused && i.productSku === sku);
}

/**
 * The schedule line that a new `sku` should be *priced* against — their own
 * line for it, else a sibling to co-terminate with. Undefined when they have
 * nothing suitable in that license family.
 *
 * Don't use this to name a product; see {@link findScheduleLineForExactSku}.
 */
export function findScheduleLineForSku(
  schedule: RenewalScheduleItem[],
  sku: string,
): RenewalScheduleItem | undefined {
  if (!sku) return undefined;

  const exact = findScheduleLineForExactSku(schedule, sku);
  if (exact) return exact;

  const active = schedule.filter((i) => !i.paused);
  const category = getLicenseCategoryForSku(sku);
  if (!category) return undefined;

  const term = termForSku(sku);
  const family = active.filter(
    (i) => skuMatchesCategory(i.productSku, category.catalogCategory) && billsOnTerm(i, term),
  );
  if (family.length === 0) return undefined;

  return family.reduce((best, i) =>
    new Date(i.nextRun).getTime() < new Date(best.nextRun).getTime() ? i : best,
  );
}
